import { useEffect, useRef, useState } from 'react';

import _ from 'lodash';

import { SCROLL_DELTA_THRESHOLD, TOP_THRESHOLD } from './constants';

/**
 * Quick-return behavior for the mobile bottom nav: hide while the user scrolls DOWN (reading deeper
 * into the page) and reveal on scroll UP. Listens in the capture phase so it reacts to whichever
 * element actually scrolls on the current page (window, a table container, a detail panel), and
 * tracks each scroller's last position independently so switching scrollers doesn't false-trigger.
 *
 * @param {string} resetKey - when it changes (e.g. the route path) the bar resets to visible
 * @returns {boolean} whether the bar should currently be hidden
 */
export const useHideOnScroll = resetKey => {
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollByTarget = useRef(new WeakMap());

  useEffect(() => {
    setIsHidden(false);
  }, [resetKey]);

  useEffect(() => {
    const onScroll = event => {
      const target = event.target === document ? document.scrollingElement : event.target;

      if (!target || !_.isNumber(target.scrollTop)) {
        return;
      }

      const { scrollTop } = target;
      const previous = lastScrollByTarget.current.get(target) ?? scrollTop;
      const delta = scrollTop - previous;

      lastScrollByTarget.current.set(target, scrollTop);

      if (Math.abs(delta) < SCROLL_DELTA_THRESHOLD) {
        return;
      }

      setIsHidden(scrollTop > TOP_THRESHOLD && delta > 0);
    };

    document.addEventListener('scroll', onScroll, { capture: true, passive: true });

    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  return isHidden;
};
